// src/utils/amplify-compat.js
import { Amplify } from 'aws-amplify'
import { fetchAuthSession } from 'aws-amplify/auth'
import config from '@/config'

const sleep = (ms) => new Promise(r => setTimeout(r, ms))

export { Amplify }

export function clearIdentityCache() {
  const idPool = config.identityPoolId
  const keys = []
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)
    if (!k) continue
    // common v5/v6 patterns
    if (k === `CognitoIdentityId-${idPool}`) keys.push(k)
    if (k === `aws.cognito.identity-id.${idPool}`) keys.push(k)
    if (k.includes(idPool) && /identity[-_:]?id/i.test(k)) keys.push(k)
    if (/amplify.*federated.*info/i.test(k)) keys.push(k) // older federated cache
  }
  // dedupe + delete
  Array.from(new Set(keys)).forEach(k => localStorage.removeItem(k))
  // sessionStorage sometimes also gets a copy:
  try {
    sessionStorage.removeItem(`CognitoIdentityId-${idPool}`)
    sessionStorage.removeItem(`aws.cognito.identity-id.${idPool}`)
  } catch (e) {
    console.debug('sessionStorage.clear failed', e)
  }
}

async function fetchFreshSession() {
  try {
    const { credentials, identityId } = await fetchAuthSession({ forceRefresh: true })
    return { ...credentials, identityId }
  } catch (e) {
    if (e?.name === 'NotAuthorizedException' && /logins don'?t match/i.test(String(e?.message))) {
      clearIdentityCache()
      await sleep(5)
      const { credentials, identityId } = await fetchAuthSession({ forceRefresh: true })
      return { ...credentials, identityId }
    }
    throw e
  }
}

export const Auth = {
  clearIdentityCache,
  async currentCredentials() {
    return fetchFreshSession()
  },
  essentialCredentials(c) {
    return {
      accessKeyId: c.accessKeyId,
      secretAccessKey: c.secretAccessKey,
      sessionToken: c.sessionToken,
    }
  },
  // Returns an AWS SDK v3 credentials provider function that transparently refreshes
  // STS tokens before they expire, allowing uploads longer than the default 1-hour TTL.
  makeLiveCredentials() {
    let cached = null

    const refresh = async () => { cached = await fetchFreshSession() }

    return async () => {
      const now = Date.now()
      const expiry = cached?.expiration instanceof Date ? cached.expiration.getTime() : 0
      if (!cached || expiry - now < 5 * 60 * 1000) {
        await refresh()
      }
      return {
        accessKeyId: cached.accessKeyId,
        secretAccessKey: cached.secretAccessKey,
        sessionToken: cached.sessionToken,
        expiration: cached.expiration,
      }
    }
  },
}
